import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CardContent, CardTitle, Button, Image } from '@thd-olt-component-react/core-ui';
import { clickEvent, impressionEvent } from '../publisher';
import {
  cardImageUrl,
  creditCardTypeText,
  isCommercial,
  analyticsAccountSnapshot,
} from '../plccUtils';
import './account-snapshot.style.scss';

const makeAPayment = 'payments';
const viewStatements = 'statements';
const manageInvoices = 'invoices';
const viewSpecialOffers = 'specialoffers';

const AccountSnapshot = ({
  redirectToCiti,
  accountNumber,
  dueDate,
  currentBalance,
  availableCredit,
  creditLimit,
  accountType,
  totalOpenInvoiceCount,
  redirectToThdMyAccount,
}) => {
  useEffect(() => {
    impressionEvent(analyticsAccountSnapshot[accountType]);
  }, []);

  const currentBalanceValue = parseFloat(currentBalance.replace(',', ''));
  const creditLimitValue = parseFloat(creditLimit.replace(',', ''));

  const cardAndCardNumberContent = () => {
    return (
      <div className="account-snapshot-flex u--paddingBottom
        u--paddingTop account-snapshot-margin-bottom u__border--bottom"
      >
        <Image
          src={cardImageUrl[accountType]}
          className="u--paddingRight u--paddingBottom"
          alt="Credit Card Icon"
          height="30"
          width="48"
        />
        <p className="u--paddingBottom">
          <span className="u__bold">{creditCardTypeText[accountType]} </span>
          (•••• {accountNumber || '---'})
        </p>
      </div>
    );
  };

  const noBalance = () => {
    return (
      <div className="account-snapshot-spacing">
        {cardAndCardNumberContent()}
        <div>
          <div className="u__text-align--center available-credit">
            <p>Available Credit</p>
            <h3 className="u__husky u__bold">${availableCredit}</h3>
          </div>
          <p className="u--paddingTop u__text-align--center">
            You don’t have a balance on your {creditCardTypeText[accountType]}{' '}
            right now.
          </p>
        </div>
        <div className="account-snapshot-flex account-snapshot-footer u__border--top">
          {accountType === 'PLCN_HOMEDEPOT' && (
            <button
              className="u__default-link"
              onClick={() => {
                clickEvent('plcc-account snapshot view special offers');
                redirectToCiti(viewSpecialOffers, accountType);
              }}
              type="button"
            >
              View Special Offers
            </button>
          )}
          {accountType !== 'PLCN_HOMEDEPOT' && (
            <button
              className="u__default-link"
              onClick={() => {
                clickEvent('plcc-account snapshot view statements');
                redirectToCiti(viewStatements, accountType);
              }}
              type="button"
            >
              View Previous Statements
            </button>
          )}
        </div>
      </div>
    );
  };

  const currentBalanceUI = () => {
    const percentCreditUsed = currentBalanceValue / creditLimitValue;

    return (
      <div className="account-snapshot-spacing">
        {cardAndCardNumberContent()}
        <div className="u__text-align--center">
          <div className="account-snapshot-flex">
            <svg width="10" height="10" className="u--paddingRight">
              <circle
                cx="50%"
                cy="50%"
                r="5"
                fill="#00873C"
                data-automation-id="status-circle"
                data-testid="status-circle"
              />
            </svg>
            <p>Available Credit</p>
          </div>
          <h3 className="u__husky u__bold">${availableCredit}</h3>
          <p>out of ${creditLimit}</p>
          <svg width="260" height="15" className="u--padding">
            <rect x="7.5" y="3" width="245" height="9" fill="#D8D8D8" />
            <circle cx="252.5" cy="7.5" r="7.5" fill="#D8D8D8" />
            <rect
              x="7.5"
              y="3"
              width={245 * percentCreditUsed}
              height="9"
              fill="#00873C"
            />
            <circle cx="7.5" cy="7.5" r="7.5" fill="#00873C" />
          </svg>
          <div className="account-snapshot-flex">
            <svg width="10" height="10" className="u--paddingRight">
              <circle cx="50%" cy="50%" r="5" fill="#D8D8D8" />
            </svg>
            <p>Current Balance</p>
          </div>
          <p>${currentBalance}</p>
        </div>
        <div
          className="account-snapshot-flex account-snapshot-footer account-snapshot-footer-space-between u__border--top"
        >
          {accountType !== 'PLNP_HOMEDEPOT' && (
            <>
              <p>Next Payment Due on {dueDate}</p>
              <button
                className="u__default-link"
                onClick={() => {
                  clickEvent('plcc-account snapshot make payment');
                  redirectToCiti(makeAPayment, accountType);
                }}
                type="button"
              >
                Make A Payment
              </button>
            </>
          )}
          {accountType === 'PLNP_HOMEDEPOT' && (
            <>
              <p>{totalOpenInvoiceCount} Open Invoices</p>
              <button
                className="u__default-link"
                onClick={() => {
                  redirectToCiti(manageInvoices, accountType);
                }}
                type="button"
              >
                Manage Invoices
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const exceededBalanceUI = () => {
    return (
      <div className="account-snapshot-spacing account-snapshot-padding-bottom">
        {cardAndCardNumberContent()}
        <div className="u__text-align--center">
          <div className="account-snapshot-flex">
            <Image
              src="https://assets.thdstatic.com/images/v1/alert-error-red.svg"
              className="account-snapshot-paddingXsmall-right"
              alt=""
              height="23"
              width="23"
            />
            <p className="u__bold account-snapshot-alert">
              Credit Limit Reached
            </p>
          </div>
          <p className="account-snapshot-alert">
            You&apos;ve reached your credit limit of ${creditLimit}
          </p>
        </div>
        <div className="u__text-align--center">
          <p className="u--paddingTop">Current Balance</p>
          <h3 className="u__husky u__bold u--paddingTop u--paddingBottom">${currentBalance}</h3>
        </div>
        {accountType !== 'PLNP_HOMEDEPOT' && (
          <>
            <div className="u__text-align--center">
              <p>Next Payment Due on {dueDate}</p>
              <Button
                className="make-a-payment-button"
                onClick={() => {
                  clickEvent('plcc-account snapshot make payment');
                  redirectToCiti(makeAPayment, accountType);
                }}
              >
                Make A Payment
              </Button>
            </div>
          </>
        )}
        {accountType === 'PLNP_HOMEDEPOT' && (
          <>
            <div className="u__text-align--center">
              <p>{totalOpenInvoiceCount} Open Invoices</p>
              <Button
                className="make-a-payment-button"
                onClick={() => {
                  redirectToCiti(manageInvoices, accountType);
                }}
              >
                Manage Invoices
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="available-credit-flex">
        <CardTitle title="My Home Depot Credit Card" />
        {/* eslint-disable-next-line */}
        <a
          className="view-account-details"
          onClick={() => {
            clickEvent('plcc-account snapshot view details');
            redirectToThdMyAccount();
          }}
        >
          <div className="u__default-link">
            {isCommercial(accountType)
              ? 'Manage Accounts'
              : 'View Account Details'}
          </div>
        </a>
      </div>
      <CardContent
        title="My Home Depot Credit Card"
        maxHeight="none"
        className="col__12-12 u--paddingNone account-snapshot"
      >
        {currentBalanceValue === 0 && noBalance()}
        {currentBalanceValue > 0 && (currentBalanceValue < creditLimitValue) && currentBalanceUI()}
        {currentBalanceValue >= creditLimitValue && exceededBalanceUI()}
      </CardContent>
    </>
  );
};

AccountSnapshot.propTypes = {
  redirectToCiti: PropTypes.func.isRequired,
  accountNumber: PropTypes.string,
  dueDate: PropTypes.string,
  currentBalance: PropTypes.string.isRequired,
  availableCredit: PropTypes.string.isRequired,
  creditLimit: PropTypes.string.isRequired,
  accountType: PropTypes.string,
  totalOpenInvoiceCount: PropTypes.string,
  redirectToThdMyAccount: PropTypes.func.isRequired,
};
AccountSnapshot.defaultProps = {
  accountNumber: null,
  accountType: 'PLCN_HOMEDEPOT',
  totalOpenInvoiceCount: '0',
  dueDate: '',
};

export default AccountSnapshot;
